import { useMemo, useState, useRef } from "react";
import { Calculator, Info } from "lucide-react";
import Reveal from "./Reveal";
import { trackEvent } from "../lib/analytics";

// Indicative mortgage calculator. Yearly costs incl. heffingen are an estimate.
const PRICE = 739000;
const KOSTEN_KOPER_PCT = 0.06; // overdrachtsbelasting + bijkomende kosten

function formatEUR(n) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function monthlyPayment(loan, annualRate, years) {
  if (loan <= 0) return 0;
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return loan / n;
  return (loan * r) / (1 - Math.pow(1 + r, -n));
}

export default function MortgageCalculator() {
  const [eigenGeld, setEigenGeld] = useState(80000);
  const [rente, setRente] = useState(3.9);
  const [looptijd, setLooptijd] = useState(30);
  const trackedRef = useRef(false);

  const handleChange = (setter, field) => (value) => {
    setter(value);
    if (!trackedRef.current) {
      trackedRef.current = true;
      trackEvent("mortgage_interact", { first_change: field });
    }
  };

  const data = useMemo(() => {
    const kostenKoper = PRICE * KOSTEN_KOPER_PCT;
    const totaalNodig = PRICE + kostenKoper;
    const lening = Math.max(PRICE - eigenGeld, 0);
    const maandlast = monthlyPayment(lening, rente, looptijd);
    const benodigdEigenGeld = Math.max(kostenKoper, 0);
    return { kostenKoper, totaalNodig, lening, maandlast, benodigdEigenGeld };
  }, [eigenGeld, rente, looptijd]);

  return (
    <section
      id="hypotheek"
      data-testid="mortgage-section"
      className="bg-brand-surface py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <p className="overline">Hypotheek-indicator</p>
            <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
              Wat zou deze woning <span className="italic">u maandelijks kosten?</span>
            </h2>
            <p className="mt-6 max-w-xl text-lg text-brand-muted">
              Speel met onderstaande variabelen voor een indicatie van de maandlast. Resultaat is een richtgetal — voor een precieze berekening adviseren wij een gesprek met uw hypotheekadviseur.
            </p>

            <div className="mt-10 flex items-center gap-3 text-xs text-brand-muted">
              <Info size={14} className="text-brand-moss" />
              Op basis van een annuïtaire hypotheek over de gehele looptijd.
            </div>
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.12}>
            <div className="border border-brand-border bg-brand-bg p-8 md:p-10">
              <div className="flex items-center gap-3">
                <Calculator size={18} className="text-brand-moss" />
                <span className="overline">Berekening</span>
              </div>

              {/* Sliders */}
              <div className="mt-8 space-y-8">
                <SliderField
                  label="Eigen geld inleg"
                  value={eigenGeld}
                  min={0}
                  max={300000}
                  step={5000}
                  onChange={handleChange(setEigenGeld, "eigen_geld")}
                  formatValue={formatEUR}
                  testId="slider-eigen-geld"
                />
                <SliderField
                  label="Rentepercentage"
                  value={rente}
                  min={2.5}
                  max={6}
                  step={0.1}
                  onChange={handleChange(setRente, "rente")}
                  formatValue={(v) => `${v.toFixed(1)} %`}
                  testId="slider-rente"
                />
                <SliderField
                  label="Looptijd"
                  value={looptijd}
                  min={10}
                  max={30}
                  step={1}
                  onChange={handleChange(setLooptijd, "looptijd")}
                  formatValue={(v) => `${v} jaar`}
                  testId="slider-looptijd"
                />
              </div>

              {/* Result */}
              <div className="mt-10 border-t border-brand-border pt-8">
                <p className="overline">Geschatte maandlast</p>
                <p
                  data-testid="mortgage-result"
                  className="mt-3 font-serif text-5xl text-brand-ink sm:text-6xl"
                >
                  {formatEUR(data.maandlast)}
                  <span className="font-sans text-base text-brand-muted"> / maand</span>
                </p>

                <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
                  <div className="border-l border-brand-border pl-4">
                    <p className="overline">Vraagprijs</p>
                    <p className="mt-1 font-serif text-xl text-brand-ink">{formatEUR(PRICE)}</p>
                  </div>
                  <div className="border-l border-brand-border pl-4">
                    <p className="overline">Kosten koper (~6%)</p>
                    <p className="mt-1 font-serif text-xl text-brand-ink">{formatEUR(data.kostenKoper)}</p>
                  </div>
                  <div className="border-l border-brand-border pl-4">
                    <p className="overline">Lening</p>
                    <p className="mt-1 font-serif text-xl text-brand-ink">{formatEUR(data.lening)}</p>
                  </div>
                  <div className="border-l border-brand-border pl-4">
                    <p className="overline">Eigen geld nodig</p>
                    <p className="mt-1 font-serif text-xl text-brand-ink">
                      {formatEUR(Math.max(eigenGeld, data.benodigdEigenGeld))}
                    </p>
                  </div>
                </div>

                <p className="mt-8 text-xs leading-relaxed text-brand-muted">
                  Dit is een indicatieve berekening op basis van een annuïtaire hypotheek. Werkelijke maandlasten hangen af van uw situatie, hypotheekvorm, NHG-grens, fiscale aftrek en bijkomende kosten. Vraag advies bij een onafhankelijk hypotheekadviseur.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function SliderField({ label, value, min, max, step, onChange, formatValue, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-brand-ink">{label}</label>
        <span className="font-serif text-2xl text-brand-moss">{formatValue(value)}</span>
      </div>
      <input
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="mt-3 h-1 w-full cursor-pointer appearance-none bg-brand-border accent-brand-moss"
      />
      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wider text-brand-muted">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
}
